// Web Audio synthesized SFX for answer feedback. Two short cues:
//
//   playCorrect — E5 → G5 two-tone ding (the spec's "ding")
//   playWrong   — C5 → A4 soft descent (non-punitive — kids should not feel
//                 scolded by the audio)
//
// Lazy AudioContext: most browsers gate AudioContext creation behind a user
// gesture, and the practice page only fires sounds in response to taps, so
// initialization on first call is safe. If AudioContext is unavailable
// (jsdom, very old browsers, denied permission), every entry point silently
// no-ops — sound is non-essential.
//
// Volume defaults are deliberately conservative; kids on phone speakers in a
// shared classroom should not blast the room.

import { useSoundStore } from './soundStore.ts'

type AudioCtor = typeof AudioContext
declare global {
  interface Window {
    webkitAudioContext?: AudioCtor
  }
}

let ctxRef: AudioContext | null = null

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (ctxRef) return ctxRef
  const Ctor: AudioCtor | undefined = window.AudioContext ?? window.webkitAudioContext
  if (!Ctor) return null
  try {
    ctxRef = new Ctor()
  } catch {
    return null
  }
  return ctxRef
}

interface ToneOpts {
  freq: number
  durationMs: number
  startDelayMs?: number
  /** Peak gain in 0–1. Defaults are quiet on purpose. */
  peak?: number
  type?: OscillatorType
}

function playTone(ctx: AudioContext, opts: ToneOpts): void {
  const { freq, durationMs, startDelayMs = 0, peak = 0.18, type = 'sine' } = opts
  const start = ctx.currentTime + startDelayMs / 1000
  const stop = start + durationMs / 1000

  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = type
  osc.frequency.setValueAtTime(freq, start)
  osc.connect(gain)
  gain.connect(ctx.destination)

  // Quick attack + exponential-ish decay. Avoids the click that bare
  // start/stop would produce.
  gain.gain.setValueAtTime(0, start)
  gain.gain.linearRampToValueAtTime(peak, start + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.0001, stop)

  osc.start(start)
  osc.stop(stop + 0.02)
}

/** Two-tone E5 → G5 ding, ~300ms total. Fires only if sound is enabled. */
export function playCorrect(): void {
  if (!useSoundStore.getState().enabled) return
  const ctx = getCtx()
  if (!ctx) return
  // Resume in case the context was suspended on creation (Safari quirk).
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }
  playTone(ctx, { freq: 659.25, durationMs: 180 })
  playTone(ctx, { freq: 783.99, durationMs: 240, startDelayMs: 90 })
}

/**
 * Triumphant 4-note arpeggio C5 → E5 → G5 → C6 — the spec's "moment #3"
 * cue when a kid finishes coloring. Slightly louder peak than per-question
 * dings since this is the closure beat for the whole flow.
 */
export function playArpeggio(): void {
  if (!useSoundStore.getState().enabled) return
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }
  const notes = [
    { freq: 523.25, delay: 0 }, // C5
    { freq: 659.25, delay: 130 }, // E5
    { freq: 783.99, delay: 260 }, // G5
    { freq: 1046.5, delay: 390 }, // C6
  ]
  for (const { freq, delay } of notes) {
    playTone(ctx, { freq, durationMs: 320, startDelayMs: delay, peak: 0.22 })
  }
}

/**
 * Spec moment #2 cue — a topic just unlocked. Bright C5 → G5 → C6 ascent
 * (perfect 5th + octave), shorter than the completion arpeggio so the
 * confetti + overlay take the lead. Distinct from playCorrect (two-tone)
 * and playArpeggio (four-tone closure).
 */
export function playUnlock(): void {
  if (!useSoundStore.getState().enabled) return
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }
  const notes = [
    { freq: 523.25, delay: 0 }, // C5
    { freq: 783.99, delay: 110 }, // G5
    { freq: 1046.5, delay: 220 }, // C6
  ]
  for (const { freq, delay } of notes) {
    playTone(ctx, { freq, durationMs: 280, startDelayMs: delay, peak: 0.2 })
  }
}

/**
 * Step 13 capstone moment — louder, longer cousin of playUnlock with an
 * added sparkle voice. Spec: ≥1.5× duration of unlock, plus a high-frequency
 * triangle sparkle layer that reads as "extra special". Fires when a pack's
 * bonus reveal phase enters.
 */
export function capstoneFlourish(): void {
  if (!useSoundStore.getState().enabled) return
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }
  // Main ascent: C5 → E5 → G5 → C6 (longer than unlock's 3 notes).
  // Total ~880ms ≈ 1.7× playUnlock. peak slightly higher because this is
  // the rarest, most celebratory cue in the app.
  const notes = [
    { freq: 523.25, delay: 0 }, // C5
    { freq: 659.25, delay: 140 }, // E5
    { freq: 783.99, delay: 280 }, // G5
    { freq: 1046.5, delay: 420 }, // C6
  ]
  for (const { freq, delay } of notes) {
    playTone(ctx, { freq, durationMs: 460, startDelayMs: delay, peak: 0.22 })
  }
  // Sparkle layer: a pair of high triangle blips, detuned, riding on top of
  // the ascent. Quiet (peak 0.06) so they read as glitter, not noise.
  playTone(ctx, {
    freq: 2093, // C7
    durationMs: 120,
    startDelayMs: 60,
    peak: 0.06,
    type: 'triangle',
  })
  playTone(ctx, {
    freq: 3136, // G7
    durationMs: 140,
    startDelayMs: 340,
    peak: 0.05,
    type: 'triangle',
  })
}

/** Soft C5 → A4 descent, slower envelope. Non-punitive. */
export function playWrong(): void {
  if (!useSoundStore.getState().enabled) return
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }
  playTone(ctx, { freq: 523.25, durationMs: 240, peak: 0.14 })
  playTone(ctx, { freq: 440, durationMs: 320, startDelayMs: 130, peak: 0.14 })
}

// ---------- practice-loop-v2 cues ----------

/**
 * Combo tier chime — a rising figure that climbs with the tier (1: two
 * notes, 2: three, 3: four). The hosanna variant (donkey passive) adds a
 * bright triangle sparkle on top. Layered over playCorrect, so it stays
 * short and sits slightly quieter.
 */
export function playComboTier(tier: 1 | 2 | 3, hosanna = false): void {
  if (!useSoundStore.getState().enabled) return
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') void ctx.resume()
  // C5 E5 G5 C6 ladder — take (tier + 1) steps.
  const ladder = [523.25, 659.25, 783.99, 1046.5]
  ladder.slice(0, tier + 1).forEach((freq, i) => {
    playTone(ctx, { freq, durationMs: 140, startDelayMs: 70 * i, peak: 0.12 })
  })
  if (hosanna) {
    playTone(ctx, { freq: 2093, durationMs: 200, startDelayMs: 70 * (tier + 1), peak: 0.06, type: 'triangle' })
  }
}

/** Sparrow shield — two soft, low, comforting notes (G4 → E4). Gentle, not celebratory. */
export function playSparrowShield(): void {
  if (!useSoundStore.getState().enabled) return
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') void ctx.resume()
  playTone(ctx, { freq: 392, durationMs: 200, peak: 0.1 })
  playTone(ctx, { freq: 329.63, durationMs: 260, startDelayMs: 110, peak: 0.1 })
}

/** Eagle soar — quick upward sweep (A4 → E5 → A5) for a retried question answered right. */
export function playEagleSoar(): void {
  if (!useSoundStore.getState().enabled) return
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') void ctx.resume()
  playTone(ctx, { freq: 440, durationMs: 120, peak: 0.11 })
  playTone(ctx, { freq: 659.25, durationMs: 130, startDelayMs: 80, peak: 0.12 })
  playTone(ctx, { freq: 880, durationMs: 220, startDelayMs: 170, peak: 0.12 })
}
